import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { SkillSchema } from "@pax/world";
import { z } from "zod";
import { type ContractService, errors, paxRoute, sessionRequired, staffAuth } from "../../common";
import {
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import {
  AnswerJoinRequestRequest,
  CreateGameRequest,
  CreateGameResponse,
  ForkGameRequest,
  ForkGameResponse,
  GameIdParams,
  GameMemberParams,
  GetGameInfoResponse,
  GetGameResponse,
  JoinGameResponse,
  ListGamesQuery,
  ListGamesResponse,
  ListJoinRequestsResponse,
  ListMembershipsQuery,
  ListMembershipsResponse,
  ListMembersResponse,
  LobbyCommandResponse,
  SelectGameMasterRequest,
  SetGameModeRequest,
  SetGameModeResponse,
  SkillParams,
  UpdateGameRequest,
  UpdateGameResponse,
  UpdateSkillRequest,
} from "./schemas";

export const service = {
  group: "games",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Games API",
  description: "Owned games and the connection used to play them.",
} as const satisfies ContractService;

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1",
      operationId: "games.list",
      summary: "List the current user's games.",
      description:
        "Joins the current user's memberships to Postgres game-directory rows, newest first.",
      inputStructure: "detailed",
      successDescription: "The user's games.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
  })
  .input(z.object({ query: ListGamesQuery }))
  .output(ListGamesResponse);

export const create = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/games/v1alpha1",
      operationId: "games.create",
      summary: "Create a game.",
      description:
        "Copies the preset version's world into game storage and records the game with its owner as the only member. The owner then sets the game up in play.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new game.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.notFound,
    ...errors.conflict,
  })
  .input(z.object({ body: CreateGameRequest }))
  .output(
    z.union([
      z.object({
        status: z.literal(200).meta({ description: "The existing identical game." }),
        body: CreateGameResponse,
      }),
      z.object({
        status: z.literal(201).meta({ description: "The newly created game." }),
        body: CreateGameResponse,
      }),
    ]),
  );

export const get = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1/{id}",
      operationId: "games.get",
      summary: "Get one game.",
      description: "Joins one Postgres game-directory row to the current user's membership.",
      inputStructure: "detailed",
      successDescription: "The game.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(GetGameResponse);

export const update = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/games/v1alpha1/{id}",
      operationId: "games.update",
      summary: "Change a game's settings.",
      description:
        "Lets a member rename the game, with `renameGame`, or change who its link lets in, with `manageJoining`. The API holds both, so the game's session need not be running; anyone connected sees the change at once. A new title is judged as the member's live input first.",
      inputStructure: "detailed",
      successDescription: "The game, changed.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The member's roles don't allow that change." },
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
  })
  .input(z.object({ params: GameIdParams, body: UpdateGameRequest }))
  .output(UpdateGameResponse);

export const setMode = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/games/v1alpha1/{id}/mode",
      operationId: "games.mode.set",
      summary: "Make a game singleplayer or multiplayer.",
      description:
        "Lets the owner open the game to others, once a review passes, or make it singleplayer again, which removes everyone else at once and ends a review. The API holds the mode, so the game's session need not be running; anyone connected sees the change at once.",
      inputStructure: "detailed",
      successDescription: "The game in its new mode.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "Only the game owner can change its mode." },
    ...surfaceBanned,
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
    CONFLICT: { message: "The game is being reviewed." },
    MODERATION_REFUSED: { status: 422, message: "The review didn't open the game to others." },
    BAD_GATEWAY: { message: "The game couldn't be reached to check it. Try again." },
  })
  .input(z.object({ params: GameIdParams, body: SetGameModeRequest }))
  .output(SetGameModeResponse);

export const remove = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/games/v1alpha1/{id}",
      operationId: "games.delete",
      summary: "Delete one game.",
      description:
        "Lets the owner delete the game: its directory rows and stored objects at once, then its session's storage. The game server need not be reachable.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The game was deleted.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "Only the game owner can delete it." },
  })
  .input(z.object({ params: GameIdParams }));

export const join = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/games/v1alpha1/{id}/join",
      operationId: "games.join",
      summary: "Join a game.",
      description:
        "Makes the current user a member of the game its link names, when the game is multiplayer and its `whoCanJoin` lets them in. Otherwise it records their ask, which a member with `manageJoining` answers, and returns `asking`; calling again returns `joined` once they're let in. A game that isn't multiplayer returns `singleplayer` and records nothing. A guest is refused with `ACCOUNT_REQUIRED` unless they are already a member. A new member spectates until they claim a perspective.",
      inputStructure: "detailed",
      successDescription: "The game with the current user's membership, or that they're asking.",
    }),
  )
  .errors({
    ...sessionRequired,
    ACCOUNT_REQUIRED: {
      status: 403,
      message: "Create an account to join someone else's game.",
    },
    NOT_FOUND: { message: "The game does not exist." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(JoinGameResponse);

export const removeMember = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/games/v1alpha1/{id}/members/{userId}",
      operationId: "games.members.delete",
      summary: "Remove someone from a game.",
      description:
        "Takes someone out of the game at once: off their perspective, out of every connection, and out of the members. Anyone but the owner may remove themselves, which is leaving. Removing someone else needs `removePeople` and a rank above theirs. They can join again if the game's link lets them in.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "They're out of the game.",
    }),
  )
  .errors({
    ...sessionRequired,
    FORBIDDEN: { message: "The member's roles don't let them remove this person." },
    NOT_FOUND: { message: "The game does not exist, or one of the two is not a member." },
  })
  .input(z.object({ params: GameMemberParams }));

export const listJoinRequests = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1/{id}/join-requests",
      operationId: "games.joinRequests.list",
      summary: "List who is asking to join.",
      description: "Lets a member whose roles allow `manageJoining` see everyone asking to join.",
      inputStructure: "detailed",
      successDescription: "Everyone asking.",
    }),
  )
  .errors({
    ...sessionRequired,
    FORBIDDEN: { message: "The member's roles don't allow managing who joins." },
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(ListJoinRequestsResponse);

export const answerJoinRequest = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/games/v1alpha1/{id}/join-requests/{userId}/answer",
      operationId: "games.joinRequests.answer",
      summary: "Answer an ask to join.",
      description:
        "Lets a member whose roles allow `manageJoining` let someone in, which makes them a member, or turn them away.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The ask is answered.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The member's roles don't allow managing who joins." },
    NOT_FOUND: { message: "The game, or the ask, does not exist." },
  })
  .input(z.object({ params: GameMemberParams, body: AnswerJoinRequestRequest }));

export const listMembers = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1/{id}/members",
      operationId: "games.members.list",
      summary: "List the game's members.",
      description:
        "Lets any member see everyone in the game, their profile, and the perspective each plays.",
      inputStructure: "detailed",
      successDescription: "The game's members.",
    }),
  )
  .errors({
    ...sessionRequired,
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(ListMembersResponse);

export const listMemberships = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1/memberships",
      operationId: "games.memberships.list",
      summary: "List a user's games.",
      description:
        "Returns a page of the games one user owns or has joined, most recently active first. Staff use it to find a player's games.",
      inputStructure: "detailed",
      successDescription: "One page of the user's games.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ query: ListMembershipsQuery }))
  .output(ListMembershipsResponse);

export const getGameInfo = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/games/v1alpha1/{id}/game-info",
      operationId: "games.gameInfo.get",
      summary: "Read a game's title, preset, and owner.",
      description:
        "Returns one game's title, preset, and owner whether or not the caller is a member. Staff use it beside the game's holds.",
      inputStructure: "detailed",
      successDescription: "The game's title, preset, and owner.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No game has that id." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(GetGameInfoResponse);

const lobbyErrors = {
  ...errors.badRequest,
  ...sessionRequired,
  FORBIDDEN: { message: "The member's roles don't allow it." },
  NOT_FOUND: { message: "The game, or what the command names, does not exist." },
  CONFLICT: { message: "The world rejected the command." },
} as const;

export const fork = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/games/v1alpha1/{id}/fork",
      operationId: "games.fork",
      summary: "Fork a game.",
      description:
        "Lets a member whose roles allow `forkGame` start a new game of their own from a revision of this one. The new game keeps the record through that revision, the same perspective, and the same game master; this game is unchanged.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new game.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The member's roles don't allow forking." },
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
    CONFLICT: {
      message: "The revision is not in the record, falls inside a jump, or the new id is taken.",
    },
  })
  .input(z.object({ params: GameIdParams, body: ForkGameRequest }))
  .output(ForkGameResponse);

export const createSkill = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/games/v1alpha1/{id}/skills",
      operationId: "games.skills.create",
      summary: "Create a skill.",
      description:
        "Lets a member whose roles allow `editRules` add a skill. Rules are skills; the game master reads all of them.",
      inputStructure: "detailed",
      successDescription: "The revision the new skill committed through.",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: GameIdParams, body: SkillSchema }))
  .output(LobbyCommandResponse);

export const updateSkill = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/games/v1alpha1/{id}/skills/{skillId}",
      operationId: "games.skills.update",
      summary: "Update a skill.",
      description:
        "Lets a member whose roles allow `editRules` change some of a skill's fields, as long as nobody has changed the skill since the revision the change was based on.",
      inputStructure: "detailed",
      successDescription: "The revision the change committed through.",
    }),
  )
  .errors({
    ...lobbyErrors,
    PRECONDITION_FAILED: {
      message: "Someone changed the skill after the revision this was based on.",
    },
  })
  .input(z.object({ params: SkillParams, body: UpdateSkillRequest }))
  .output(LobbyCommandResponse);

export const deleteSkill = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/games/v1alpha1/{id}/skills/{skillId}",
      operationId: "games.skills.delete",
      summary: "Delete a skill.",
      description: "Lets a member whose roles allow `editRules` remove one of the game's skills.",
      inputStructure: "detailed",
      successDescription: "The revision the removal committed through.",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: SkillParams }))
  .output(LobbyCommandResponse);

export const setGameMaster = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/games/v1alpha1/{id}/game-master",
      operationId: "games.gameMaster.set",
      summary: "Choose the game master.",
      description:
        "Lets a member whose roles allow `changeGameMaster` put a new agent of a published version in the game-master role, replacing the one there.",
      inputStructure: "detailed",
      successDescription: "The revision the selection committed through.",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: GameIdParams, body: SelectGameMasterRequest }))
  .output(LobbyCommandResponse);
